/**
 * `atr 5 sma 20` — an indicator, its settings and a smoother, typed in one go.
 *
 * The command search's half of Add indicator (owner, 2026-10-18). The grammar,
 * in the order it is read:
 *
 * | typed | means |
 * |---|---|
 * | `atr 5` | ATR (5) |
 * | `macd 5 35 5` | MACD (5, 35, 5) — numbers fill the leading numeric params in order |
 * | `sma 50 20 8` | three SMAs — a one-number family takes each number as its own instance |
 * | `atr 5 with sma 20`, `atr 5 on sma 20` | ATR (5) with an SMA (20) **of** the ATR |
 * | `atr 5, sma 20`, `atr 5 and sma 20` | two indicators: ATR (5), and SMA (20) on price |
 * | `atr 5 sma 20` | **both readings**, the smoother first and "separately" second |
 *
 * **Only the leading numeric params are typed.** A choice, a symbol or a flag
 * stops the run and keeps its default — `sma 50` cannot reach the source, and
 * `rsi 14` never has to guess which word means which choice.
 *
 * **A smoother is the alias suffix, not a second indicator** — `atr_5~sma20`,
 * which `addSmoother` mints exactly as the picker's Smooth does. So it carries
 * that grammar's limits and is checked by its own parser (`splitSmoothed`):
 * SMA or EMA, a whole length from 2 to 500, and one level — a smoother of a
 * smoother is no reading at all. Of a multi-output family it averages the
 * **first** output (MACD's MACD line, not its signal), which is the line the
 * picker's Smooth averages too.
 *
 * **Juxtaposition is the one ambiguity**, and it is not resolved silently:
 * `atr 5 sma 20` is both readings, the rule's (a smoother) first. `with`/`on`
 * and `,`/`and` each say which they mean, so each gives one.
 *
 * Pure — no React, no dispatch. The palette builds rows from
 * {@link parseIndicatorPhrase}; the action applies a plan through
 * {@link addIndicatorPlan}, which is the only place a chart changes.
 */

import type { ChartGroup } from "@qufolio/model";

import type { IndicatorEntry } from "../api/indicators";
import { addIndicator, addSmoother, DEFAULT_SMOOTHER, indicatorAliases, type IndicatorParams } from "./add-indicator";
import { SMOOTH_KINDS, splitSmoothed, type SmoothKind } from "./move-to-pane";

/** A moving average of one indicator's first line. */
export interface Smooth {
  kind: SmoothKind;
  length: number;
}

/** One indicator to add: the family, the numbers typed for it, and its smoother. */
export interface PlanItem {
  entry: IndicatorEntry;
  /** The leading numeric params, in order; the rest keep their defaults. */
  params: readonly number[];
  smooth?: Smooth;
}

/** One way of reading what was typed — one palette row. */
export interface PhraseReading {
  /** Stable for a list key: the aliases the reading would mint, joined. */
  key: string;
  items: readonly PlanItem[];
  /** As the legend names it: `ATR (5) with SMA (20)`. */
  title: string;
  /** Where it lands: `own pane`, `on price`, `pane + price`, `adds all three`. */
  where: string;
}

// ── the catalog's side ──────────────────────────────────────────────────────

/** A family by its name, then its label, case-insensitive — the action's own lookup. */
export function findEntry(catalog: readonly IndicatorEntry[], word: string): IndicatorEntry | undefined {
  const wanted = word.trim().toLowerCase();
  if (!wanted) return undefined;
  return (
    catalog.find((e) => e.name.toLowerCase() === wanted) ?? catalog.find((e) => e.label.toLowerCase() === wanted)
  );
}

/**
 * How many numbers a family takes: its leading run of numeric params. A
 * choice, a symbol or a flag ends it — `sma` is one (its source follows).
 */
export function numericSlots(entry: IndicatorEntry): number {
  const params = entry.params ?? [];
  let n = 0;
  while (n < params.length && (params[n]!.kind ?? "number") === "number") n++;
  return n;
}

/** Typed numbers onto the param names `addIndicator` reads; the rest default. */
export function paramsByName(entry: IndicatorEntry, numbers: readonly number[]): IndicatorParams {
  const out: Record<string, number> = {};
  numbers.forEach((value, i) => {
    const spec = entry.params?.[i];
    if (spec) out[spec.name] = value;
  });
  return out;
}

/** `MACD (5, 35, 5)` — the leading numerics as they will be added, defaults filled. */
export function legendTitle(entry: IndicatorEntry, numbers: readonly number[]): string {
  const slots = numericSlots(entry);
  if (slots === 0) return entry.label;
  const values = (entry.params ?? []).slice(0, slots).map((spec, i) => numbers[i] ?? Number(spec.default));
  return `${entry.label} (${values.join(", ")})`;
}

/** Whether a smoother of this kind and length is one the alias grammar accepts. */
export function validSmooth(kind: string, length: number): kind is SmoothKind {
  return (SMOOTH_KINDS as readonly string[]).includes(kind) && splitSmoothed(`x~${kind}${length}`) !== undefined;
}

// ── applying a plan ─────────────────────────────────────────────────────────

/**
 * Every item added to `group`, smoothers included, as **one** new group — so
 * the caller dispatches once and one undo takes the whole pick back.
 *
 * Each step is `add-indicator.ts`'s own: `addIndicator` for the family (alias
 * `atr_5`), then `addSmoother` on the plot it just added for the first output
 * (`atr_5~sma20`). A step that changes nothing is a refusal, never a skip — a
 * pick that added two of three things would say it worked.
 */
export function addIndicatorPlan(
  group: ChartGroup,
  items: readonly PlanItem[],
  mintId: () => string,
): { group: ChartGroup } | { reason: string } {
  let next = group;
  for (const item of items) {
    const params = paramsByName(item.entry, item.params);
    const before = new Set(next.panes.flatMap((pane) => (pane.plots ?? []).map((plot) => plot.id)));
    const added = addIndicator(next, item.entry, params, mintId);
    if (added === next) return { reason: `${item.entry.label} could not be added to this chart` };
    next = added;
    if (!item.smooth) continue;
    // The family's first output — the line the picker's Smooth averages.
    const first = indicatorAliases(item.entry, params)[0];
    const fresh = next.panes.flatMap((pane) => pane.plots ?? []).filter((plot) => !before.has(plot.id));
    const source = fresh.find((plot) => plot.outputAlias === first) ?? fresh[0];
    const smoothed = source ? addSmoother(next, source.id, item.smooth.kind, item.smooth.length, mintId) : next;
    if (smoothed === next) {
      return { reason: `${legendTitle(item.entry, item.params)} could not be smoothed with ${smoothTitle(item.smooth)}` };
    }
    next = smoothed;
  }
  return { group: next };
}

// ── reading a phrase ────────────────────────────────────────────────────────

const NUMBER = /^\d+(?:\.\d+)?$/;
const SEPARATORS = new Set([",", "and"]);
const SMOOTHING = new Set(["with", "on"]);
/** The longest label tried as one family — `bollinger bands` is two words. */
const MAX_LABEL_WORDS = 4;

/** How a family was joined to the one before it. */
type Joiner = "start" | "separate" | "smooth" | "juxtaposed";

interface Segment {
  /** Absent only for a bare `sma`/`ema` the catalog does not carry — a smoother still. */
  entry?: IndicatorEntry;
  /** Set when the word was `sma` or `ema`, which may smooth what precedes it. */
  smoother?: SmoothKind;
  numbers: number[];
  joiner: Joiner;
}

/**
 * The phrase as families, each with its numbers and how it was joined; `null`
 * for an unknown word, a stray number or a dangling `with`.
 */
function segments(text: string, catalog: readonly IndicatorEntry[]): Segment[] | null {
  const tokens = text.toLowerCase().replace(/,/g, " , ").split(/\s+/).filter(Boolean);
  const out: Segment[] = [];
  let joiner: Joiner | null = null;
  for (let i = 0; i < tokens.length; ) {
    const token = tokens[i]!;
    if (NUMBER.test(token)) {
      // A number belongs to the family before it; one after a joiner has none.
      if (out.length === 0 || joiner) return null;
      out[out.length - 1]!.numbers.push(Number(token));
      i++;
      continue;
    }
    // The longest run of words that names a family, so a label with a space
    // (or an `and` in it) wins over its first word read as a joiner.
    let hit: { entry: IndicatorEntry; width: number } | undefined;
    for (let width = Math.min(MAX_LABEL_WORDS, tokens.length - i); width >= 1 && !hit; width--) {
      const words = tokens.slice(i, i + width);
      if (words.some((w) => w === "," || NUMBER.test(w))) continue;
      const entry = findEntry(catalog, words.join(" "));
      if (entry) hit = { entry, width };
    }
    if (!hit && (SMOOTH_KINDS as readonly string[]).includes(token)) {
      // A smoother needs no catalog entry — it is an alias suffix, not a family.
      out.push({ smoother: token as SmoothKind, numbers: [], joiner: joiner ?? (out.length === 0 ? "start" : "juxtaposed") });
      joiner = null;
      i++;
      continue;
    }
    if (!hit) {
      if (out.length === 0 || joiner) return null;
      if (SEPARATORS.has(token)) joiner = "separate";
      else if (SMOOTHING.has(token)) joiner = "smooth";
      else return null;
      i++;
      continue;
    }
    const smoother =
      hit.width === 1 && (SMOOTH_KINDS as readonly string[]).includes(token) ? (token as SmoothKind) : undefined;
    out.push({
      entry: hit.entry,
      ...(smoother ? { smoother } : {}),
      numbers: [],
      joiner: joiner ?? (out.length === 0 ? "start" : "juxtaposed"),
    });
    joiner = null;
    i += hit.width;
  }
  return joiner ? null : out;
}

/** A segment as indicators: one, or one per number for a one-number family. */
function instances(segment: Segment): PlanItem[] | null {
  const { numbers, entry } = segment;
  if (!entry) return null;
  const slots = numericSlots(entry);
  if (numbers.length <= slots) return [{ entry, params: numbers }];
  if (slots === 1) return numbers.map((n) => ({ entry, params: [n] }));
  return null;
}

const isSmootherWord = (segment: Segment) => segment.smoother !== undefined;

/** The segments as a plan, juxtaposed SMA/EMA read as smoothers or not; `null` if illegal. */
function plan(parts: readonly Segment[], smoothJuxtaposed: boolean): PlanItem[] | null {
  const items: PlanItem[] = [];
  for (const part of parts) {
    const smooth = part.joiner === "smooth" || (part.joiner === "juxtaposed" && smoothJuxtaposed && isSmootherWord(part));
    if (!smooth) {
      const more = instances(part);
      if (!more) return null;
      items.push(...more);
      continue;
    }
    // A smoother: SMA/EMA, at most one length, of the indicator just before —
    // which must not itself be smoothed (one level).
    const last = items[items.length - 1];
    if (!last || last.smooth || !isSmootherWord(part) || part.numbers.length > 1) return null;
    const length = part.numbers[0] ?? DEFAULT_SMOOTHER.length;
    if (!validSmooth(part.smoother!, length)) return null;
    items[items.length - 1] = { ...last, smooth: { kind: part.smoother!, length } };
  }
  return items;
}

const smoothTitle = (smooth: Smooth) => `${smooth.kind.toUpperCase()} (${smooth.length})`;

const itemTitle = (item: PlanItem) =>
  item.smooth
    ? `${legendTitle(item.entry, item.params)} with ${smoothTitle(item.smooth)}`
    : legendTitle(item.entry, item.params);

const COUNT_WORDS = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];

/** Where a plan lands, as the mock says it. */
function whereOf(items: readonly PlanItem[]): string {
  if (items.some((item) => item.entry.locked)) return "not included in your plan";
  if (items.length === 1) return items[0]!.entry.placement === "price" ? "on price" : "own pane";
  if (items.every((item) => item.entry === items[0]!.entry)) {
    return `adds all ${COUNT_WORDS[items.length] ?? items.length}`;
  }
  const panes = items.filter((item) => item.entry.placement !== "price").length;
  const parts = [panes === 1 ? "pane" : panes > 1 ? `${panes} panes` : "", panes < items.length ? "price" : ""];
  return parts.filter(Boolean).join(" + ");
}

/** The aliases-ish identity of a plan, for a row key. */
function planKey(items: readonly PlanItem[]): string {
  return items
    .map((item) => {
      const head = [item.entry.name, ...item.params].join("_");
      return item.smooth ? `${head}~${item.smooth.kind}${item.smooth.length}` : head;
    })
    .join("+");
}

/**
 * Every reading of a typed phrase, the rule's first — `[]` when it is no
 * phrase at all.
 *
 * **A bare name is not a phrase**: `rsi` is the catalog row the palette already
 * lists, and a second row for the same thing would only push the list down. A
 * phrase has a number or a second family.
 */
export function parseIndicatorPhrase(text: string, catalog: readonly IndicatorEntry[]): PhraseReading[] {
  if (catalog.length === 0) return [];
  const parts = segments(text, catalog);
  if (!parts || parts.length === 0) return [];
  if (parts.length === 1 && parts[0]!.numbers.length === 0) return [];

  const ambiguous = parts.some((p) => p.joiner === "juxtaposed" && isSmootherWord(p));
  const readings = ambiguous ? [plan(parts, true), plan(parts, false)] : [plan(parts, true)];
  const legal = readings.filter((r): r is PlanItem[] => r !== null && r.length > 0);
  const out: PhraseReading[] = [];
  for (const items of legal) {
    const key = planKey(items);
    if (out.some((r) => r.key === key)) continue;
    const titles = items.map(itemTitle);
    // The alternative to a smoother says so; an unambiguous list reads as one.
    const separately = ambiguous && legal.length > 1 && items.length > 1 && !items.some((i) => i.smooth);
    out.push({
      key,
      items,
      title: separately ? `${titles.join(", ")} separately` : titles.join(" · "),
      where: whereOf(items),
    });
  }
  return out;
}

/** A reading as `addIndicator`'s args: the first item, the rest under `also`. */
export function phraseArgs(items: readonly PlanItem[]): Record<string, unknown> {
  const one = (item: PlanItem) => ({
    indicator: item.entry.name,
    ...(item.params.length ? { params: [...item.params] } : {}),
    ...(item.smooth ? { smooth: { ...item.smooth } } : {}),
  });
  const [first, ...rest] = items;
  return { ...one(first!), ...(rest.length ? { also: rest.map(one) } : {}) };
}
